import sgSignature from "../../../assets/sgsignature.jpeg";

/* ── date / month helpers ─────────────────────────────────────────────── */

export function ordSuffix(n: number) {
  const v = n % 100;
  const s = ["th", "st", "nd", "rd"];
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export const MONTHS = [
  "JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE",
  "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER",
];

export const MONTHS_TC = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Derive "Month, Year" from the diet name, e.g. "MRI March Diet 2026" → "March, 2026".
 * Falls back to the application dates, then the raw title.
 */
export function formatDietMonth(diet: any): string {
  const title = String(diet?.title ?? diet?.name ?? "");

  const yearMatch  = title.match(/\b(20\d{2})\b/);
  const monthMatch = title.match(
    /\b(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t)?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)\b/i,
  );

  if (monthMatch && yearMatch) {
    const token = monthMatch[1].slice(0, 3).toLowerCase();
    const idx   = MONTHS_TC.findIndex((m) => m.slice(0, 3).toLowerCase() === token);
    if (idx >= 0) return `${MONTHS_TC[idx]}, ${yearMatch[1]}`;
  }

  const raw = diet?.applications_open_at ?? diet?.applications_close_at;
  if (raw) {
    const d = new Date(raw);
    if (!isNaN(d.getTime())) return `${MONTHS_TC[d.getMonth()]}, ${d.getFullYear()}`;
  }

  return title;
}

export function formatIssuedDate(dateStr?: string | null): string {
  let d = dateStr ? new Date(dateStr) : new Date();
  if (isNaN(d.getTime())) d = new Date();
  return `${ordSuffix(d.getDate())} ${MONTHS[d.getMonth()]}, ${d.getFullYear()}`;
}

/* ── per-diet letter templates ────────────────────────────────────────── */

export const DEFAULT_SUBJECT = "NOTIFICATION OF {exam_upper} - {month}";
export const DEFAULT_BODY_RECOMMENDED =
  "We are pleased to inform you that you were successful in the {exam} conducted by the Institute in {month}.\n\nKindly visit the Institute's website to view the requirements for membership upgrade and apply accordingly.\n\nCongratulations.";
export const DEFAULT_BODY_NOT_RECOMMENDED =
  "We regret to inform you that you were not successful in the {exam} conducted by the Institute in {month}. You are therefore NOT RECOMMENDED for membership upgrade at this time.\n\nYou may re-present yourself for the interview in a subsequent diet.";

// Replace {placeholders} in a template with letter values.
export function substVars(tpl: string, vars: Record<string, string>): string {
  return String(tpl ?? "").replace(/\{(\w+)\}/g, (m, k) => vars[k] ?? m);
}

// Split free text into paragraphs on blank lines.
export function toParagraphs(text: string): string[] {
  return String(text ?? "")
    .split(/\n\s*\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function escapeHtml(s: string): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Per-diet result-letter settings, with sensible fallbacks. */
export function letterSettings(diet: any) {
  return {
    signName: diet?.result_signatory_name || "QS Ahmad Kabir Muhammad, FNIQS",
    signTitle: diet?.result_signatory_title || "Secretary General",
    refPrefix: diet?.result_ref_prefix || "NIQS/ECB/08C/A/",
    issued: formatIssuedDate(diet?.result_issued_date),
    sigUrl: diet?.result_signature_url || null,
    subjectTpl: diet?.result_subject || DEFAULT_SUBJECT,
    bodyRecTpl: diet?.result_body_recommended || DEFAULT_BODY_RECOMMENDED,
    bodyNotRecTpl: diet?.result_body_not_recommended || DEFAULT_BODY_NOT_RECOMMENDED,
  };
}

/* ── result status helpers ────────────────────────────────────────────── */

// Interview (non paper-based) outcomes that produce a result letter.
export const INTERVIEW_STATUSES = [
  "recommended",
  "not_recommended",
  "partial_pass",
  "full_reset",
  "absent",
];

export function isInterviewStatus(s?: string | null): boolean {
  return INTERVIEW_STATUSES.includes(String(s ?? ""));
}

// Only a clear pass uses the "recommended" success letter body; every other
// outcome (not_recommended, partial_pass, full_reset, absent, failed) uses the
// "not recommended" body.
export function isPositiveResult(s?: string | null): boolean {
  return ["recommended", "passed"].includes(String(s ?? ""));
}

export const RESULT_LABELS: Record<string, string> = {
  recommended: "Recommended",
  not_recommended: "Not Recommended",
  partial_pass: "Partial Pass",
  // Passed most subjects but not all — carried into a later diet rather than
  // re-sitting the lot.
  referred: "Referred",
  full_reset: "Full Reset",
  absent: "Absent",
  passed: "Passed",
  failed: "Failed",
  pending: "Pending",
};

export function resultLabel(s?: string | null): string {
  const k = String(s ?? "");
  return RESULT_LABELS[k] ?? k.replace(/_/g, " ");
}

// The result letter is the MRI/PCI interview notification — only these exams
// produce it.
export function isInterviewExam(examCode?: string | null): boolean {
  return ["MRI", "PCI"].includes((examCode ?? "").toUpperCase());
}

/**
 * The same question asked of a whole exam rather than a bare code.
 *
 * `is_interview` is the exam's own answer and wins where the payload carries
 * it; the code is only the fallback for the older payloads that select
 * `id,code,title` and nothing else. An exam flagged as an interview under some
 * other code was reading as course-based here — and a course-based exam is
 * handed a STATEMENT OF RESULT, which would have printed subject lines for a
 * sitting that has no subjects.
 */
export function isInterview(exam?: { code?: string | null; is_interview?: boolean | number | null } | null): boolean {
  if (exam?.is_interview !== undefined && exam?.is_interview !== null) {
    return !!exam.is_interview;
  }

  return isInterviewExam(exam?.code);
}

/**
 * The exam as it should read on a letter.
 *
 * PCI and MRI have fixed interview names. Anything else is a course-based exam,
 * whose own title is the only honest label — defaulting those to "Mature Route
 * Interview" would put the wrong exam on a candidate's letter.
 */
export function examLabelFor(examCode?: string | null, examTitle?: string | null): string {
  const code = (examCode ?? "").toUpperCase();
  if (code === "PCI") return "Professional Competence Interview (PCI)";
  if (code === "MRI") return "Mature Route Interview";
  return examTitle || code || "Examination";
}

/** Placeholder substitution values shared by print + preview. */
export function letterVars(app: any, diet: any) {
  const memberNo  = app.user?.member?.membership_no ?? "—";
  const name      = (app.user?.name ?? "").toUpperCase();
  const examLabel = examLabelFor(app.exam?.code, app.exam?.title);
  return {
    name,
    exam: examLabel,
    exam_upper: examLabel.toUpperCase(),
    month: formatDietMonth(diet),
    membership_no: memberNo,
  } as Record<string, string>;
}

/**
 * The Institute's printed letterhead — logo, wordmark, gold arc and address as
 * one artwork, so a printed result matches the stationery exactly rather than
 * being reassembled from a logo and a typed address block that drift apart.
 *
 * Served from `public/`, so it is fetched by URL rather than bundled.
 */
const LETTERHEAD_FILE = "/niqs-letterhead.png";

function letterheadImg(): string {
  const src = `${window.location.origin}${LETTERHEAD_FILE}`;
  // If the artwork is ever missing, a broken-image icon at the top of an
  // official result is worse than no banner at all.
  return `<img src="${src}" class="letterhead" alt="The Nigerian Institute of Quantity Surveyors"
    onerror="this.style.display='none'"/>`;
}

/** The interpretation legend that sits under the subjects table. */
const RESULT_CODE_LEGEND: [string, string][] = [
  ["P", "PASSED"],
  ["PP", "PASSED PREVIOUSLY"],
  ["RF", "REFERRED"],
  ["EX", "EXEMPTED"],
  ["F", "FAIL"],
  ["ABS", "ABSENT"],
  ["CANC", "CANCELLED"],
];

/**
 * The statement of result a course-based exam (TPC) produces: the subjects the
 * candidate sat and the code each was awarded, over the Institute's letterhead.
 *
 * This is the whole printout for a course exam — not an appendix to a
 * recommendation letter. Only PCI/MRI get a letter, because only they carry a
 * recommendation.
 */
function buildStatementOfResult(app: any, diet: any): string {
  const memberNo  = app.user?.member?.membership_no ?? "—";
  const name      = (app.user?.name ?? "").toUpperCase();
  const address   = (app.user?.member?.address ?? "").toUpperCase().replace(/\n/g, ",<br/>");
  const examLabel = examLabelFor(app.exam?.code, app.exam?.title).toUpperCase();
  // "September, 2025" reads as "SEPTEMBER 2025" in the statement heading.
  const dietMonth = formatDietMonth(diet).replace(/,/g, "").toUpperCase();
  const { signName, signTitle, issued, sigUrl } = letterSettings(diet);
  const sigSrc  = sigUrl || `${window.location.origin}${sgSignature}`;

  // An interview has no subjects. Its single unit arrives with a null course
  // id, and legacy data can leave real course rows on an exam later flagged as
  // an interview — neither belongs on a statement for a sitting that was one
  // decision, so nothing is tabulated for one at all.
  const courses: any[] = isInterview(app?.exam)
    ? []
    : (Array.isArray(app?.result?.course_breakdown) ? app.result.course_breakdown : [])
        .filter((c: any) => c?.course_id != null);

  const rows = courses
    .map(
      (c) => `    <tr>
      <td>${escapeHtml(c.course_name ?? "—")}</td>
      <td class="code">${escapeHtml(c.grade ?? "—")}</td>
    </tr>`,
    )
    .join("\n");

  const legend = RESULT_CODE_LEGEND.map(
    ([code, meaning]) => `    <div><span class="lg-code">${code}</span>${meaning}</div>`,
  ).join("\n");

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Statement of Result — ${escapeHtml(name)}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: "Times New Roman", serif; font-size: 12pt; color: #000; background: #fff; padding: 40px 60px; }
  .letterhead { display: block; width: 100%; height: auto; margin-bottom: 26px; }
  h1 { text-align: center; font-size: 19pt; letter-spacing: .02em; margin-bottom: 18px; }
  .exam-line { text-align: center; font-weight: bold; font-size: 12pt; margin-bottom: 26px; }
  .meta-row { display: flex; justify-content: space-between; font-weight: bold; margin-bottom: 20px; }
  .candidate { font-weight: bold; margin-bottom: 10px; }
  .addr { line-height: 1.5; margin-bottom: 22px; }
  .certify { line-height: 1.7; margin-bottom: 22px; text-align: justify; }
  table.subjects { width: 100%; border-collapse: collapse; font-size: 11.5pt; }
  table.subjects th { text-transform: uppercase; font-size: 10pt; letter-spacing: .04em; padding: 0 0 6px; border: 0; }
  table.subjects th.code { text-align: right; }
  table.subjects td { border: 1px solid #000; padding: 5px 8px; }
  table.subjects td.code { width: 54px; text-align: center; border-left: 1px solid #000; }
  .legend { margin: 26px 0 0 auto; width: 260px; font-size: 10pt; line-height: 1.55; }
  .legend .lg-title { font-weight: bold; text-decoration: underline; margin-bottom: 4px; }
  .legend .lg-code { display: inline-block; width: 58px; }
  .sig-block { margin-top: 30px; }
  .sig-img { height: 60px; display: block; margin-bottom: 2px; }
  .sig-name { font-weight: bold; }
  .disclaimer { margin-top: 26px; font-size: 9pt; color: #c00; }
  @media print {
    body { padding: 20px 40px; }
    /* A result split across a page seam reads as two different results. */
    table.subjects { page-break-inside: avoid; }
  }
</style>
</head>
<body>
${letterheadImg()}

<h1>STATEMENT OF RESULT</h1>
<div class="exam-line">${escapeHtml(examLabel)} ${escapeHtml(dietMonth)}</div>

<div class="meta-row">
  <span>EXAM NO: ${escapeHtml(app?.exam_no ?? "—")}</span>
  <span>DATE ISSUED: ${escapeHtml(issued)}.</span>
</div>

<div class="candidate">${escapeHtml(name)}, ${escapeHtml(memberNo)}</div>
<div class="addr">${address || "&nbsp;"}</div>

<p class="certify">
  This is to certify that the above named candidate sat for this examination and obtained the results as
  shown below:
</p>

<table class="subjects">
  <thead>
    <tr><th>Subjects</th><th class="code">Result</th></tr>
  </thead>
  <tbody>
${rows || '    <tr><td>No subjects recorded</td><td class="code">—</td></tr>'}
  </tbody>
</table>

<div class="legend">
  <div class="lg-title">INTERPRETATION</div>
${legend}
</div>

<div class="sig-block">
  <img src="${sigSrc}" class="sig-img" alt="Signature"/>
  <div class="sig-name">${escapeHtml(signName)}</div>
  <div>${escapeHtml(signTitle)}</div>
</div>

<div class="disclaimer">* Any alteration or erasure on this statement of result invalidates it.</div>
</body>
</html>`;
}

/** Open a built document in a print window. */
function openPrintWindow(html: string, failureMessage: string) {
  const win = window.open("", "_blank", "width=900,height=1100");
  if (!win) {
    window.alert(failureMessage);
    return;
  }
  win.document.write(html);
  win.document.close();
  win.focus();
  // The delay lets the logo and signature load; printing an empty-image page
  // was the reason this wait exists.
  setTimeout(() => { win.print(); }, 400);
}

/**
 * Print a candidate's result.
 *
 * PCI and MRI produce a recommendation LETTER — the outcome is a recommendation
 * for upgrade, and there are no subjects to tabulate. Every other exam is
 * course-based and produces a STATEMENT OF RESULT instead: the subjects sat and
 * the code awarded for each. They are different documents, not one document
 * with an optional appendix.
 *
 * `app` must carry user.member (membership_no/address), exam.code and result;
 * `diet` carries the per-diet letter settings.
 */
export function printResultLetter(app: any, diet: any) {
  if (!isInterview(app?.exam)) {
    openPrintWindow(
      buildStatementOfResult(app, diet),
      "Please allow pop-ups to print the statement of result.",
    );
    return;
  }

  return printRecommendationLetter(app, diet);
}

function printRecommendationLetter(app: any, diet: any) {
  const memberNo  = app.user?.member?.membership_no ?? "—";
  const name      = (app.user?.name ?? "").toUpperCase();
  const address   = (app.user?.member?.address ?? "").replace(/\n/g, "<br/>");
  const examLabel = examLabelFor(app.exam?.code, app.exam?.title);
  const dietMonth = formatDietMonth(diet);
  const { signName, signTitle, refPrefix, issued, sigUrl, subjectTpl, bodyRecTpl, bodyNotRecTpl } = letterSettings(diet);
  const ref       = `${refPrefix}${memberNo}`;
  const sigSrc  = sigUrl || `${window.location.origin}${sgSignature}`;
  const recommended = isPositiveResult(app.result?.overall_status);

  const vars: Record<string, string> = {
    name,
    exam: examLabel,
    exam_upper: examLabel.toUpperCase(),
    month: dietMonth,
    membership_no: memberNo,
  };
  const subject = substVars(subjectTpl, vars);
  const bodyHtml = toParagraphs(substVars(recommended ? bodyRecTpl : bodyNotRecTpl, vars))
    .map((p) => `<p>${escapeHtml(p)}</p>`)
    .join("\n");

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Result Letter — ${name}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: "Times New Roman", serif; font-size: 13pt; color: #000; background: #fff; padding: 40px 60px; }
  .letterhead { display: block; width: 100%; height: auto; border-bottom: 2px solid #000; padding-bottom: 16px; margin-bottom: 24px; }
  .ref-row { display: flex; justify-content: space-between; margin: 20px 0 10px; font-weight: bold; }
  .recipient { margin: 16px 0 6px; font-weight: bold; }
  .addr { margin-bottom: 24px; line-height: 1.6; }
  .subject { font-weight: bold; text-decoration: underline; margin: 24px 0 16px; }
  p { margin-bottom: 14px; line-height: 1.7; text-align: justify; }
  .closing { margin-top: 20px; margin-bottom: 16px; }
  .sig-img { height: 60px; display: block; margin-bottom: 4px; }
  .sig-name { font-weight: bold; }
  .disclaimer { margin-top: 50px; font-size: 9pt; font-style: italic; border-top: 1px solid #888; padding-top: 8px; }

  @media print { body { padding: 20px 40px; } }
</style>
</head>
<body>
${letterheadImg()}

<div class="ref-row">
  <span>${ref}</span>
  <span>DATE ISSUED: ${issued}.</span>
</div>

<div class="recipient">${name}</div>
<div style="font-weight:bold; margin:4px 0 8px;">MEMBERSHIP NUMBER: ${memberNo}</div>
<div class="addr">${address || "&nbsp;"}</div>

<div class="subject">
  ${escapeHtml(subject)}
</div>

${bodyHtml}

<p class="closing" style="margin-top:24px; margin-bottom:4px;">Yours faithfully,</p>
<p style="font-weight:bold; margin-bottom:6px;">For: The Nigerian Institute of Quantity Surveyors</p>
<img src="${sigSrc}" class="sig-img" alt="Signature"/>
<div class="sig-name">${signName}</div>
<div>${signTitle}</div>

<div class="disclaimer">* Any alteration or erasure on this statement of result invalidates it.</div>
</body>
</html>`;

  openPrintWindow(html, "Please allow pop-ups to print the result letter.");
}
